import React, { useEffect, useMemo, useState } from "react";
import {
  buildPaymentDepositCalendarCells,
  buildPaymentDepositCalendarDayMap,
  formatPaymentDepositMonthTitle,
  getKoreaDateKey,
  shiftPaymentDepositMonth,
} from "./paymentDepositCalendarUtils";
import PaymentDepositDayPopover from "./PaymentDepositDayPopover";
import { PaymentDepositCalendarRequest } from "./paymentDepositTypes";

type Props = {
  requests: PaymentDepositCalendarRequest[];
  dateFrom: string;
  dateTo: string;
};

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const compactWon = (value: number) => {
  if (value >= 10000) {
    const tenThousands = value / 10000;
    return `${Number.isInteger(tenThousands) ? tenThousands : tenThousands.toFixed(1)}만원`;
  }
  return `${value.toLocaleString("ko-KR")}원`;
};

const PaymentDepositCalendar = ({ requests, dateFrom, dateTo }: Props) => {
  const today = getKoreaDateKey();
  const preferredVisibleDate =
    (!dateFrom || today >= dateFrom) && (!dateTo || today <= dateTo)
      ? today
      : dateFrom || today;
  const [visibleMonth, setVisibleMonth] = useState(
    preferredVisibleDate.slice(0, 7)
  );
  const [activeDate, setActiveDate] = useState("");
  const [dismissedHoverDate, setDismissedHoverDate] = useState("");

  useEffect(() => {
    setVisibleMonth(preferredVisibleDate.slice(0, 7));
  }, [preferredVisibleDate]);

  const days = useMemo(
    () => buildPaymentDepositCalendarDayMap(requests),
    [requests]
  );
  const cells = useMemo(
    () =>
      buildPaymentDepositCalendarCells({
        monthKey: visibleMonth,
        dateFrom,
        dateTo,
        today,
        days,
      }),
    [dateFrom, dateTo, days, today, visibleMonth]
  );
  const activeDay = activeDate ? days.get(activeDate) : undefined;
  const closeActiveDay = () => {
    setDismissedHoverDate(activeDate);
    setActiveDate("");
  };

  return (
    <section
      aria-label="입금관리 승인일 기준 달력"
      className="mt-4 border border-slate-200 bg-slate-50 p-3 sm:p-4"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black text-emerald-700">
            승인일 기준 입금 달력
          </p>
          <h5 className="mt-1 text-base font-black text-slate-950">
            {formatPaymentDepositMonthTitle(visibleMonth)}
          </h5>
          <p className="mt-1 text-[11px] font-bold text-slate-500">
            선택한 KCP 승인일 범위 {dateFrom || "-"} ~ {dateTo || "-"}의
            거래 건수와 승인잔액 합계를 표시합니다.
          </p>
          <p className="mt-1 text-[11px] font-bold text-slate-500">
            승인일이 없는 거래는 조회 범위와 달력에서 제외됩니다. 월 이동은
            현재 조회된 승인 거래를 달력에서 확인합니다.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            aria-label="이전 달"
            onClick={() =>
              setVisibleMonth((current) =>
                shiftPaymentDepositMonth(current, -1)
              )
            }
            className="min-h-[44px] border border-slate-300 bg-white px-3 text-xs font-black text-slate-700"
          >
            이전 달
          </button>
          <button
            type="button"
            onClick={() => setVisibleMonth(today.slice(0, 7))}
            className="min-h-[44px] border border-emerald-300 bg-emerald-50 px-3 text-xs font-black text-emerald-900"
          >
            오늘
          </button>
          <button
            type="button"
            aria-label="다음 달"
            onClick={() =>
              setVisibleMonth((current) =>
                shiftPaymentDepositMonth(current, 1)
              )
            }
            className="min-h-[44px] border border-slate-300 bg-white px-3 text-xs font-black text-slate-700"
          >
            다음 달
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-7 border-l border-t border-slate-200 bg-white">
        {WEEKDAYS.map((weekday) => (
          <div
            key={weekday}
            className="border-b border-r border-slate-200 px-1 py-2 text-center text-[11px] font-black text-slate-600"
          >
            {weekday}
          </div>
        ))}
        {cells.map((cell, index) =>
          !cell.inMonth ? (
            <div
              key={`empty:${index}`}
              aria-hidden="true"
              className="min-h-[76px] border-b border-r border-slate-200 bg-slate-50 sm:min-h-[108px]"
            />
          ) : (
            <button
              key={cell.date}
              type="button"
              aria-label={`${cell.date} 입금관리 상세`}
              aria-haspopup="dialog"
              aria-expanded={activeDate === cell.date}
              onMouseEnter={() => {
                if (cell.summary && dismissedHoverDate !== cell.date) {
                  setActiveDate(cell.date);
                }
              }}
              onMouseLeave={() => {
                if (dismissedHoverDate === cell.date) {
                  setDismissedHoverDate("");
                }
              }}
              onFocus={() => {
                if (cell.summary) {
                  setDismissedHoverDate("");
                  setActiveDate(cell.date);
                }
              }}
              onClick={() => {
                setDismissedHoverDate("");
                if (cell.summary) {
                  setActiveDate((current) =>
                    current === cell.date ? "" : cell.date
                  );
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") closeActiveDay();
              }}
              className={[
                "min-h-[76px] min-w-0 border-b border-r border-slate-200 p-1 text-left align-top sm:min-h-[108px] sm:p-2",
                cell.inRange ? "bg-white" : "bg-slate-100 text-slate-400",
                cell.isToday ? "ring-2 ring-inset ring-emerald-500" : "",
                cell.summary
                  ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  : "cursor-default",
              ].join(" ")}
            >
              <span className="flex items-center justify-between gap-1 text-[11px] font-black sm:text-xs">
                {cell.dayOfMonth}
                {cell.isToday && (
                  <span className="text-[9px] text-emerald-700 sm:text-[10px]">
                    오늘
                  </span>
                )}
              </span>
              {cell.summary && (
                <span className="mt-1 block space-y-0.5 text-[9px] font-black leading-4 sm:text-[11px]">
                  {cell.summary.transactions.length > 0 && (
                    <span className="block text-slate-800">
                      거래 {cell.summary.transactions.length} ·{" "}
                      {compactWon(cell.summary.transactionAmount)}
                    </span>
                  )}
                </span>
              )}
            </button>
          )
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-black">
        <span className="text-slate-800">
          거래 = KCP 승인일 기준 건수 · 승인잔액
        </span>
      </div>

      {activeDay && (
        <PaymentDepositDayPopover
          day={activeDay}
          onClose={closeActiveDay}
        />
      )}
    </section>
  );
};

export default PaymentDepositCalendar;
